import React from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';

import Box from '@material-ui/core/Box';
import Modal from '@material-ui/core/Modal';

import {FlexGrid, FlexGridColumnGroup} from '@grapecity/wijmo.react.grid';
import {FlexGridFilter} from '@grapecity/wijmo.react.grid.filter';

import {contractDntOperations} from '../../../../../../../reducks/contractDnt/';

import PaperPart from '../../../../../../atoms/PaperPart.js';
import PositiveButton from '../../../../../../atoms/Buttons/PositiveButton.js';
import {getDataFromGeneralPurposeMap} from '../../../../../../../common/common.js';

/** 申込種別: 取付 */
const ORDER_TYPE_ATTACH = '1';

/** 申込種別: 撤去 */
const ORDER_TYPE_REMOVE = '2';

/** 申込種別: 一束化 */
const ORDER_TYPE_ISSK = '5';

// FlexGridのヘッダー用スタイル定義
const gridStyle = `
  .dnt-modal-grid .wj-header {
    white-space: pre-wrap !important;
    word-break: keep-all !important;
    overflow-wrap: normal !important;
    vertical-align: middle !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
  }
`;

/**
 * 契約電柱一覧モーダル
 *
 * @param {object} props プロパティ
 * @param {boolean} props.open モーダル表示フラグ
 * @param {function} props.onClose モーダルを閉じるための関数
 * @param {object} props.generalPurposeMap 汎用マスタデータ
 * @param {string} props.orderNo 申込番号
 * @param {string} props.orderType 申込種別
 * @param {function} props.getContractDnt 契約電柱一覧取得APIを実行するための関数
 * @param {function} props.clearContractDnt 契約電柱一覧クリアAPIを実行するための関数
 * @param {array} props.contractDnts 契約電柱一覧データ(API実行結果)
 *
 * @return {JSX.Element}
 */
function DntModal(props) {
  const {
    open,
    onClose,
    generalPurposeMap,
    orderNo,
    orderType,
    getContractDnt,
    clearContractDnt,
    contractDnts,
  } = props;

  const [dispDnts, setDispDnts] = React.useState([]);

  const gridRef = React.createRef();

  // API実行
  React.useEffect(() => {
    // 申込番号未設定時にはなにもしない
    if (!orderNo) {
      return;
    }

    // 既存データをクリア
    clearContractDnt();

    // APIを実行してデータを取得
    const conditions = {
      'OrderNo__c': orderNo,
    };

    const fields = [
      'Dsp_SenroName__c',
      'Dsp_DntNo__c',
      'toLabel(DntCategory__c) DntCategory',
      'toLabel(IskkCategory__c) IskkCategory',
      'IskkAiteZgsya1__r.Name',
      'IskkAiteZgsya2__r.Name',
      'IskkAiteZgsya3__r.Name',
      'NWZgsyo__r.Name',
      'toLabel(BefChangeIskkCategory__c) BefIskkCategory',
      'BefChangeIskkAiteZgsya1__r.Name',
      'BefChangeIskkAiteZgsya2__r.Name',
      'BefChangeIskkAiteZgsya3__r.Name',
      'toLabel(AttachType__c) AttachType',
      'toLabel(TekyoCategory__c) TekyoCategory',
    ];

    const orders = {
      'SerialNumber__c': '1',
    };

    getContractDnt(conditions, fields, orders);
  }, [orderNo, clearContractDnt, getContractDnt]);

  // 契約電柱データ取得時処理
  const {maxError, maxKensu} = React.useMemo(() => {
    if (!contractDnts || contractDnts.length === 0) {
      setDispDnts([]);
      return {maxError: false};
    }

    // 件数超過エラー
    const maxKensu = getDataFromGeneralPurposeMap(
        generalPurposeMap, 'DisplayMaxKensu1', 1).ValueNum1__c;

    const maxError = contractDnts.length >= maxKensu;

    let dispDntList = contractDnts;
    if (maxError) {
      // maxKensu件数の数だけに減らしたcontractDntsを設定する
      dispDntList = contractDnts.slice(0, maxKensu);
    }
    setDispDnts(dispDntList);

    return {maxError, maxKensu};
  }, [contractDnts, generalPurposeMap]);

  /**
   * FlexGrid初期化時の処理
   * ヘッダー行の高さを自動調整する
   * @param {FlexGrid} grid
   */
  const handleInitialized = (grid) => {
    if (grid && grid.columnHeaders) {
      // ヘッダー行を自動リサイズ
      grid.autoSizeRows(0, grid.columnHeaders.rows.length - 1, true);
    }
  };

  /**
   * モーダルのクローズ処理
   *
   * @param {object} _event イベントオブジェクト
   * @param {string} reason クローズ理由
   */
  const handleClose = async (_event, reason) => {
    // モーダル外クリック時に閉じないようにする
    if (reason !== 'buttonClick') {
      return;
    }

    await clearContractDnt();
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >

      <PaperPart
        style={{
          maxWidth: '85vw',
          maxHeight: '95vh',
          overflow: 'hidden',
          padding: '10px',
          display: 'flex',
          flexDirection: 'column',
        }}>

        <Box flex={1}>
          <style>{gridStyle}</style>
          {maxError && (
            <Box mb={1} color="error.main">
              {`契約電柱の件数が多すぎるため、${maxKensu}件まで表示しています`}
            </Box>
          )}

          <FlexGrid
            ref={gridRef}
            itemsSource={dispDnts}

            isReadOnly={true}
            autoGenerateColumns={false}
            deferResizing={false}
            showMarquee={true}
            headersVisibility="Column"
            allowDragging="None"
            allowMerging="ColumnHeaders"
            selectionMode="MultiRange"
            className="dnt-modal-grid"
            initialized={handleInitialized}
            style={{maxHeight: '80vh'}}
          >
            <FlexGridFilter></FlexGridFilter>
            <FlexGridColumnGroup header="電柱">
              <FlexGridColumnGroup header="線路名" binding="Dsp_SenroName__c" width={150} />
              <FlexGridColumnGroup header="電柱番号" binding="Dsp_DntNo__c" width={110} />
              <FlexGridColumnGroup header="電柱区分" binding="DntCategory" width={110} />
            </FlexGridColumnGroup>

            <FlexGridColumnGroup header="電線">
              <FlexGridColumnGroup header="一束化" binding="IskkCategory" width={100} />
              <FlexGridColumnGroup header="一束化相手 共架事業者1" binding="IskkAiteZgsya1__r.Name" width={140} />
              <FlexGridColumnGroup header="一束化相手 共架事業者2" binding="IskkAiteZgsya2__r.Name" width={140} />
              <FlexGridColumnGroup header="一束化相手 共架事業者3" binding="IskkAiteZgsya3__r.Name" width={140} />
            </FlexGridColumnGroup>

            {/* 申込種別が「一束化(5)」の場合 */}
            {orderType === ORDER_TYPE_ISSK && (
              <FlexGridColumnGroup header="変更前">
                <FlexGridColumnGroup header="一束化" binding="BefIskkCategory" width={100} />
                <FlexGridColumnGroup header="一束化相手 共架事業者1" binding="BefChangeIskkAiteZgsya1__r.Name" width={140} />
                <FlexGridColumnGroup header="一束化相手 共架事業者2" binding="BefChangeIskkAiteZgsya2__r.Name" width={140} />
                <FlexGridColumnGroup header="一束化相手 共架事業者3" binding="BefChangeIskkAiteZgsya3__r.Name" width={140} />
              </FlexGridColumnGroup>
            )}

            <FlexGridColumnGroup header="事業所" binding="NWZgsyo__r.Name" width={120} />

            {/* 申込種別が「取付(1)」の場合 */}
            {orderType === ORDER_TYPE_ATTACH && (
              <FlexGridColumnGroup header="取付種別" binding="AttachType" width={110} />
            )}

            {/* 申込種別が「撤去(2)」の場合 */}
            {orderType === ORDER_TYPE_REMOVE && (
              <FlexGridColumnGroup header="撤去区分" binding="TekyoCategory" width={110} />
            )}
          </FlexGrid>
        </Box>

        <Box display="flex" justifyContent="flex-end">
          <PositiveButton onClick={onClose}>閉じる</PositiveButton>
        </Box>
      </PaperPart>
    </Modal>
  );
};

DntModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  generalPurposeMap: PropTypes.object.isRequired,
  orderNo: PropTypes.string.isRequired,
  orderType: PropTypes.string.isRequired,
  getContractDnt: PropTypes.func.isRequired,
  clearContractDnt: PropTypes.func.isRequired,
  contractDnts: PropTypes.array,
};

const mapStateToProps = (state) => ({
  contractDnts: state.contractDnt.contractDntSearchList,
  generalPurposeMap: state.common.generalPurposeMap,
});

const mapDispatchToProps = {
  getContractDnt: contractDntOperations.doGetContractDntSearchList,
  clearContractDnt: contractDntOperations.doClearContractDntSearchList,
};

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(DntModal);
